import { draftInputSchema, type DraftInput } from '../../shared/drafts';
import { templateDefinitions, templateKeys, type TemplateKey } from '../../shared/templates';
import { createStarterDraft } from './editorModel';

/**
 * Dosyadan taslak içe aktarma (Milestone 033). Tamamen istemci tarafındadır: dosya okunur, Türkçe sade biçim taslak
 * biçimine çevrilir, şablon varsayılanlarıyla tamamlanır ve editörün kullandığı `draftInputSchema` ile doğrulanır.
 * Kayıt, öğretmen editörde **Kaydet**'e basınca bugünkü yeni taslak yoluyla yapılır (sunucu aynı şemayı yeniden uygular).
 * Biçim: `docs/taslak-ice-aktarma.md`.
 */
export const DRAFT_FILE_FORMAT = 'odevlendir-taslak';
export const DRAFT_FILE_VERSION = 1;
export const DRAFT_FILE_MAX_BYTES = 64 * 1024;

export type DraftImportResult = { ok: true; draft: DraftInput; questionCount: number } | { ok: false; errors: string[] };

const TOP_KEYS = ['format', 'surum', 'sablon', 'baslik', 'ayarlar', 'sorular'];
const QUESTION_KEYS = ['soru', 'secenekler', 'dogru', 'sure', 'seviye', 'vagon'];
const SETTING_KEYS = ['mod', 'sureModu', 'duseneKadarTekrar'];
/** Soru başı süre kullanabilen şablonlar ve varsayılan süreleri (sn). */
const PER_QUESTION_TIMING: Partial<Record<TemplateKey, number>> = { 'ufo-adventure': 20, 'orbital-bridge': 30, 'turbo-duel': 30 };
const MODE_TEMPLATES: Partial<Record<TemplateKey, Record<string, 'guided' | 'challenge'>>> = {
  'fiery-route': { normal: 'guided', 'uc-can': 'challenge', '3-can': 'challenge' },
  'orbital-bridge': { rehberli: 'guided', normal: 'guided', 'uc-can': 'challenge', '3-can': 'challenge' },
};
const FIELD_NAMES: Record<string, string> = {
  title: 'başlık', prompt: 'soru metni', text: 'seçenek metni', choices: 'seçenekler', timeLimitSeconds: 'süre',
  maxLevels: 'soru/seviye sayısı', questions: 'sorular', wagonStyle: 'vagon', timingMode: 'süre modu', bridgeMode: 'mod',
};

type Json = Record<string, unknown>;
const isObject = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);
const normalize = (value: string) => value.trim().toLocaleLowerCase('tr-TR');

function resolveTemplate(value: unknown): TemplateKey | null {
  if (typeof value !== 'string') return null;
  const wanted = normalize(value);
  return templateKeys.find((key) => key === value.trim() || normalize(templateDefinitions[key].name) === wanted) ?? null;
}

function templateList() { return templateKeys.map((key) => `"${key}" (${templateDefinitions[key].name})`).join(', '); }

/** JSON.parse hata iletisindeki konumdan satır numarası çıkarır. */
function jsonErrorLine(text: string, error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  const line = /line (\d+)/.exec(message)?.[1];
  const position = /position (\d+)/.exec(message)?.[1];
  if (line) return ` (${line}. satır civarı)`;
  if (position) return ` (${text.slice(0, Number(position)).split('\n').length}. satır civarı)`;
  return '';
}

/** Şema hatalarını soru/seçenek numaralı Türkçe iletiye çevirir. */
function describeIssue(issue: { code: string; path: PropertyKey[]; message: string; maximum?: unknown; minimum?: unknown; origin?: unknown }): string {
  const [first, index, field, choiceIndex] = issue.path;
  let where = '';
  if (first === 'questions' && typeof index === 'number') {
    where = `${index + 1}. soru`;
    if (field === 'choices' && typeof choiceIndex === 'number') where += `, ${choiceIndex + 1}. seçenek`;
    where += ': ';
  }
  // Alan adı: yoldaki son metin parçası (ör. questions.2.choices.1.text → "text").
  const fieldKey = String([...issue.path].reverse().find((part) => typeof part === 'string') ?? '');
  const name = FIELD_NAMES[fieldKey] ?? fieldKey;
  if (issue.code === 'custom') return where + issue.message;
  if (issue.code === 'too_big') return `${where}${name} çok ${issue.origin === 'string' ? 'uzun' : 'fazla'} (en fazla ${String(issue.maximum)}${issue.origin === 'string' ? ' karakter' : ''}).`;
  if (issue.code === 'too_small') return issue.message && !/^Too small/i.test(issue.message) ? where + issue.message : `${where}${name} çok ${issue.origin === 'string' ? 'kısa' : 'az'} (en az ${String(issue.minimum)}).`;
  return `${where}${name} alanı eksik veya hatalı.`;
}

function validate(draft: DraftInput): DraftImportResult {
  const parsed = draftInputSchema.safeParse(draft);
  if (parsed.success) return { ok: true, draft: parsed.data, questionCount: parsed.data.questions.length };
  return { ok: false, errors: [...new Set(parsed.error.issues.map((issue) => describeIssue(issue as Parameters<typeof describeIssue>[0])))] };
}

/** Seviye N'de N soru: 1, 3, 6, 10, 15 soru tam seviye doldurur. */
function trainLevels(count: number): number[] | null {
  const levels: number[] = [];
  for (let level = 1; levels.length < count && level <= 5; level += 1) for (let i = 0; i < level; i += 1) levels.push(level);
  return levels.length === count ? levels : null;
}

/** Editörün iç biçimi (gelişmiş kullanım): görsel kimlikleri taslağa özel olduğundan atılır. */
function importInternal(raw: Json): DraftImportResult {
  const questions = Array.isArray(raw.questions) ? raw.questions : [];
  const draft = { ...raw, questions: questions.map((q) => isObject(q) ? { ...q, mediaId: undefined,
    choices: Array.isArray(q.choices) ? q.choices.map((c) => isObject(c) ? { ...c, mediaId: undefined } : c) : q.choices } : q) } as DraftInput;
  return validate(draft);
}

export function importDraftFile(text: string): DraftImportResult {
  if (new TextEncoder().encode(text).length > DRAFT_FILE_MAX_BYTES) return { ok: false, errors: [`Dosya çok büyük (en fazla ${DRAFT_FILE_MAX_BYTES / 1024} KB).`] };
  let raw: unknown;
  try { raw = JSON.parse(text.replace(/^﻿/, '')); } catch (error) {
    return { ok: false, errors: [`Dosya okunamadı: geçerli bir JSON değil${jsonErrorLine(text, error)}.`] };
  }
  if (!isObject(raw)) return { ok: false, errors: ['Dosya bir JSON nesnesi olmalı ({ ... }).'] };
  if ('templateKey' in raw && 'questions' in raw) return importInternal(raw);

  const errors: string[] = [];
  const unknown = Object.keys(raw).filter((key) => !TOP_KEYS.includes(key));
  if (unknown.length) errors.push(`Tanınmayan alan: ${unknown.map((k) => `"${k}"`).join(', ')}. Geçerli alanlar: ${TOP_KEYS.join(', ')}.`);
  if (raw.format !== undefined && raw.format !== DRAFT_FILE_FORMAT) errors.push(`"format" alanı "${DRAFT_FILE_FORMAT}" olmalı.`);
  if (raw.surum !== undefined && raw.surum !== DRAFT_FILE_VERSION) errors.push(`Bu dosya sürümü (${String(raw.surum)}) desteklenmiyor; "surum": ${DRAFT_FILE_VERSION} olmalı.`);
  const templateKey = resolveTemplate(raw.sablon);
  if (!templateKey) errors.push(raw.sablon === undefined ? `"sablon" alanı eksik. Geçerli şablonlar: ${templateList()}.` : `Bilinmeyen şablon: "${String(raw.sablon)}". Geçerli şablonlar: ${templateList()}.`);
  if (typeof raw.baslik !== 'string' || !raw.baslik.trim()) errors.push('"baslik" alanı eksik: oyunun başlığını yazın.');
  const settings = raw.ayarlar === undefined ? {} : raw.ayarlar;
  if (!isObject(settings)) errors.push('"ayarlar" bir nesne olmalı ({ ... }).');
  if (!Array.isArray(raw.sorular) || raw.sorular.length === 0) errors.push('"sorular" alanı en az bir soru içeren bir liste olmalı ([ ... ]).');
  if (errors.length || !templateKey || !isObject(settings) || !Array.isArray(raw.sorular)) return { ok: false, errors };

  const starter = createStarterDraft(templateKey);
  // Ayarlar: yalnız ilgili şablonda anlamlı olanlar kabul edilir.
  const unknownSettings = Object.keys(settings).filter((key) => !SETTING_KEYS.includes(key));
  if (unknownSettings.length) errors.push(`Tanınmayan ayar: ${unknownSettings.map((k) => `"${k}"`).join(', ')}. Geçerli ayarlar: ${SETTING_KEYS.join(', ')}.`);
  let bridgeMode = starter.bridgeMode;
  if (settings.mod !== undefined) {
    const modes = MODE_TEMPLATES[templateKey];
    const mapped = modes && typeof settings.mod === 'string' ? modes[normalize(settings.mod)] : undefined;
    if (!modes) errors.push(`"mod" ayarı bu şablonda kullanılmaz (${templateDefinitions[templateKey].name}).`);
    else if (!mapped) errors.push(`"mod" için geçerli değerler: ${Object.keys(modes).map((m) => `"${m}"`).join(', ')}.`);
    else bridgeMode = mapped;
  }
  const perQuestionDefault = PER_QUESTION_TIMING[templateKey];
  const anyTime = raw.sorular.some((q) => isObject(q) && q.sure !== undefined);
  let timingMode = starter.timingMode;
  if (settings.sureModu !== undefined) {
    if (!perQuestionDefault) errors.push(`"sureModu" ayarı bu şablonda kullanılmaz (${templateDefinitions[templateKey].name}).`);
    else if (settings.sureModu === 'yok') timingMode = 'none';
    else if (settings.sureModu === 'soru-basi') timingMode = 'per-question';
    else errors.push('"sureModu" için geçerli değerler: "yok", "soru-basi".');
  } else if (anyTime && perQuestionDefault) timingMode = 'per-question';
  if (anyTime && !perQuestionDefault) errors.push(`"sure" alanı bu şablonda kullanılmaz (${templateDefinitions[templateKey].name} soru süresi kullanmaz).`);
  if (anyTime && timingMode === 'none' && perQuestionDefault) errors.push('"sure" yazılan sorular için "sureModu": "soru-basi" olmalı.');
  let repeatUntilFall: boolean | undefined;
  if (settings.duseneKadarTekrar !== undefined) {
    if (templateKey !== 'balance-arena') errors.push('"duseneKadarTekrar" ayarı yalnız Denge Arenası içindir.');
    else if (typeof settings.duseneKadarTekrar !== 'boolean') errors.push('"duseneKadarTekrar" true veya false olmalı.');
    else repeatUntilFall = settings.duseneKadarTekrar || undefined;
  }

  const train = templateKey === 'train-adventure';
  const questions: DraftInput['questions'] = [];
  raw.sorular.forEach((item, i) => {
    const at = `${i + 1}. soru: `;
    if (!isObject(item)) { errors.push(`${at}bir nesne olmalı ({ "soru": ..., "secenekler": [...], "dogru": 0 }).`); return; }
    const extra = Object.keys(item).filter((key) => !QUESTION_KEYS.includes(key));
    if (extra.length) errors.push(`${at}tanınmayan alan ${extra.map((k) => `"${k}"`).join(', ')}. Geçerli alanlar: ${QUESTION_KEYS.join(', ')}.`);
    if (typeof item.soru !== 'string' || !item.soru.trim()) errors.push(`${at}"soru" metni eksik.`);
    const choices = item.secenekler;
    if (!Array.isArray(choices) || choices.some((c) => typeof c !== 'string')) { errors.push(`${at}"secenekler" metinlerden oluşan bir liste olmalı (["...", "..."]).`); return; }
    if (!Number.isInteger(item.dogru) || (item.dogru as number) < 0 || (item.dogru as number) >= choices.length) {
      errors.push(`${at}"dogru" doğru seçeneğin sırası olmalı: ilk seçenek 0, ikinci 1… (bu soruda 0–${choices.length - 1}).`); return;
    }
    if (item.sure !== undefined && (typeof item.sure !== 'number' || !Number.isInteger(item.sure))) errors.push(`${at}"sure" saniye cinsinden tam sayı olmalı.`);
    if (item.seviye !== undefined && !train) errors.push(`${at}"seviye" yalnız Tren Macerası içindir.`);
    if (item.vagon !== undefined && (!train || !['genis', 'dar'].includes(String(item.vagon)))) errors.push(`${at}"vagon" yalnız Tren Macerası içindir ve "genis" ya da "dar" olmalı.`);
    questions.push({
      prompt: String(item.soru ?? '').trim(), levels: [Number.isInteger(item.seviye) ? item.seviye as number : i + 1],
      ...(train ? { wagonStyle: item.vagon === 'dar' ? 'narrow' as const : 'wide' as const } : {}),
      timeLimitSeconds: timingMode === 'per-question' ? (typeof item.sure === 'number' ? item.sure : perQuestionDefault ?? null) : null,
      choices: choices.map((text, j) => ({ text: String(text).trim(), isCorrect: j === item.dogru })),
    });
  });
  if (errors.length) return { ok: false, errors };

  let maxLevels = questions.length;
  if (train) {
    const explicit = raw.sorular.filter((q) => isObject(q) && q.seviye !== undefined).length;
    if (explicit === 0) {
      const levels = trainLevels(questions.length);
      if (!levels) return { ok: false, errors: [`Tren Macerası'nda seviye N'de N soru olur: "seviye" yazmadan 1, 3, 6, 10 veya 15 soru verin (dosyada ${questions.length} soru var) ya da her soruya "seviye" ekleyin.`] };
      questions.forEach((q, i) => { q.levels = [levels[i]]; });
    } else if (explicit !== questions.length) return { ok: false, errors: ['Tren Macerası: "seviye" ya bütün sorularda ya hiçbirinde yazılmalı.'] };
    maxLevels = Math.max(...questions.map((q) => q.levels[0]));
  }
  return validate({ title: String(raw.baslik).trim(), templateKey, levelMode: 'manual', maxLevels, trackTime: starter.trackTime,
    ...(bridgeMode ? { bridgeMode } : {}), ...(timingMode ? { timingMode } : {}), ...(repeatUntilFall ? { repeatUntilFall } : {}), questions });
}

/** Seçili şablon için kurallara uyan, öğretmenin üzerine yazabileceği örnek dosya. */
export function sampleDraftFile(templateKey: TemplateKey): string {
  const name = templateDefinitions[templateKey].name;
  const fourChoices = templateKey === 'billionaire-quiz';
  const count = templateKey === 'train-adventure' ? 3 : Math.max(3, createStarterDraft(templateKey).questions.length);
  const settings: Json = templateKey === 'fiery-route' ? { mod: 'normal' } : templateKey === 'orbital-bridge' ? { mod: 'rehberli', sureModu: 'yok' }
    : templateKey === 'turbo-duel' ? { sureModu: 'yok' } : templateKey === 'balance-arena' ? { duseneKadarTekrar: false }
    : templateKey === 'ufo-adventure' ? { sureModu: 'soru-basi' } : {};
  const sorular = Array.from({ length: count }, (_, i) => {
    const a = i + 2, b = i + 3, answer = a + b;
    const options = fourChoices ? [answer, answer + 1, answer - 1, answer + 2] : [answer, answer + 1, answer - 1];
    return { soru: `${a} + ${b} kaç eder?`, secenekler: options.map(String), dogru: 0,
      ...(templateKey === 'train-adventure' ? { seviye: i === 0 ? 1 : 2 } : {}),
      ...(templateKey === 'ufo-adventure' ? { sure: 20 } : {}) };
  });
  return `${JSON.stringify({ format: DRAFT_FILE_FORMAT, surum: DRAFT_FILE_VERSION, sablon: templateKey, baslik: `Örnek ${name}`,
    ...(Object.keys(settings).length ? { ayarlar: settings } : {}), sorular }, null, 2)}\n`;
}
